import { css } from "@emotion/react";
import Balancer from "react-wrap-balancer";

import { mq } from "@/constants/breakpoints";
import { darkColor } from "@/constants/colors";

import Container from "@/components/Container/Container";
import Input from "@/components/Input/Input";
import {
  Body,
  BodyMedium14,
  Heading,
  H5Css,
  H7Css,
} from "@/components/Typography";

import Button from "../Buttons/Button";
import { scheduleGeneratorToolPath } from "@/constants/paths";
import { useRouter } from "next/router";
import posthog from "posthog-js";
import { ReviewBadgeHero } from "../Homepage/ReviewBadgeHero";
import { useWindowSize } from "@/hooks/useWindowSize";

const ScheduleGeneratorHeroSection = ({ fields }: any) => {
  const router = useRouter();
  const { isXs } = useWindowSize();

  const handleSubmit = (e: any) => {
    e.preventDefault();
    const organizationName = e.target[0].value;
    sessionStorage.setItem("jw_organization_name", organizationName);
    posthog.capture("JWMarketing - SG Org Name Entered");
    router.push(scheduleGeneratorToolPath);
  };

  return (
    <Container
      css={css`
        ${mq["xs"]} {
          padding-top: 0px;
          padding-bottom: 0px;
        }
        ${mq["sm"]} {
          padding-top: 14px;
          padding-bottom: 24px;
        }
        ${mq["md"]} {
          padding-top: 14px;
          padding-bottom: 44px;
        }

        ${mq["xl"]} {
          padding-top: 60px;
          padding-bottom: 104px;
        }
      `}
    >
      <div
        css={css`
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: space-between;
          ${mq["xl"]} {
            flex-direction: row;
            justify-content: space-between;
          }
          ${mq["xs"]} {
            padding-bottom: 16px;
          }
        `}
      >
        <div
          css={css`
            max-width: 600px;
            text-align: center;
            ${mq["xl"]} {
              text-align: left;
            }
          `}
        >
          <Heading
            level={1}
            css={css`
              margin-top: 28px;
              color: #fff;
              font-weight: 400;
              font-size: 28px;
              line-height: 130%;
              width: 100%;
              ${mq["lg"]} {
                font-size: 50px;
                line-height: 120%;
              }
            `}
          >
            <Balancer
              dangerouslySetInnerHTML={{
                __html: fields.heading.replace(/\. /, ".<br>"),
              }}
            />
          </Heading>
          <Body
            css={css`
              color: #fff;
              margin-top: 14px;
              font-weight: 400;
              font-size: 16px;
              line-height: 140%;
              ${mq["lg"]} {
                margin-top: 24px;
                font-weight: 500;
                font-size: 20px;
              }
            `}
          >
            <Balancer>{fields.subheading}</Balancer>
          </Body>
          <div
            css={css`
              display: flex;
              justify-content: center;
              gap: 4px;
              width: 100%;
              margin-top: 24px;
              ${mq["xl"]} {
                justify-content: flex-start;
              }
              ${mq["md"]} {
                gap: 16px;
              }
              align-items: center;
            `}
          >
            <ReviewBadgeHero
              imageSrc="/images/icons/g2_icon.svg"
              text={`${process.env.g2Rating || "4.5"} Stars ${
                process.env.g2Count || "100"
              }+ ${isXs ? "" : "reviews"}`}
            />

            <ReviewBadgeHero
              imageSrc="/images/icons/trustpilot_icon.svg"
              text={`${process.env.trustpilotRating || "4.7"} Stars ${
                process.env.trustpilotCount || "200"
              }+ ${isXs ? "" : "reviews"}`}
            />
          </div>
        </div>
        <div
          css={css`
            ${mq["xs"]} {
              margin-top: 11px;
            }
            ${mq["sm"]} {
              margin-top: 22px;
            }
            ${mq["lg"]} {
              margin-top: 0px;
              max-width: 50%;
              width: 100%;
              margin-right: calc(-50vw + 60%);
            }
          `}
        >
          <div
            css={css`
              position: relative;
              --gapNumber: 9;
              --gap: calc(var(--gapNumber) * 1px);
              ${mq["sm"]} {
                --gapNumber: 14;
              }
              ${mq["lg"]} {
                --gapNumber: 20;
              }
              max-width: calc(680px + (var(--gap) * 2));
              margin: auto;
              ${mq["xs"]} {
                margin-top: 40px;
              }
              ${mq["lg"]} {
                max-width: calc(680px + (var(--gap) * 2));
                width: 100%;
              }
            `}
          >
            <div
              css={css`
                position: relative;
                // padding: var(--gap);
                * {
                  position: relative;
                  z-index: 0;
                }
                div {
                  background: linear-gradient(
                    103.92deg,
                    rgba(255, 255, 255, 0.1) -0.74%,
                    rgba(190, 220, 255, 0.1) 99.87%
                  );
                  backdrop-filter: blur(39.206px);
                }
              `}
            >
              <div
                css={css`
                  position: absolute;
                  bottom: calc(var(--gap) * 5);
                  right: calc(var(--gap) * 0.5);
                  width: calc(100% - (var(--gap) * 3.5));
                  height: calc(100% - (var(--gap) * 2));
                  border-radius: 20px;
                  border: 0.98px solid rgba(255, 255, 255, 0.1);

                  ${mq["xs"]} {
                    display: none;
                  }
                  ${mq["sm"]} {
                    border-radius: 17.7px;
                  }
                  ${mq["lg"]} {
                    border-radius: 25px;
                  }
                  ${mq["xl"]} {
                    display: block;
                    bottom: calc(var(--gap) * 3.2);
                    right: calc(var(--gap) * -2.1);
                    width: calc(100% - (var(--gap) * 0.8));
                    height: calc(100% - (var(--gap) * 0.8));
                  }
                `}
              />
              <div
                css={css`
                  position: absolute;
                  border: 0.98px solid rgba(255, 255, 255, 0.1);
                  border-radius: 20px;

                  ${mq["xs"]} {
                    bottom: calc(var(--gap) * 2.4);
                    right: calc(var(--gap) * -2);
                    width: calc(100% - (var(--gap) * 0.5));
                    height: calc(100% - (var(--gap) * 0.5));
                  }
                  ${mq["sm"]} {
                    bottom: calc(var(--gap) * 2.4);
                    right: calc(var(--gap) * -1.4);
                    width: calc(100% - (var(--gap) * 0.5));
                    height: calc(100% - (var(--gap) * 1));
                  }
                  ${mq["md"]} {
                    bottom: calc(var(--gap) * 2.4);
                    right: calc(var(--gap) * -1.6);
                    width: calc(100% - (var(--gap) * 1));
                    height: calc(100% - (var(--gap) * 0.9));
                  }
                  ${mq["lg"]} {
                    bottom: calc(var(--gap) * 2.2);
                    right: calc(var(--gap) * -1);
                    width: calc(100% - (var(--gap) * 1));
                    height: calc(100% - (var(--gap) * 1));
                  }
                  ${mq["xl"]} {
                    bottom: calc(var(--gap) * 1.5);
                    right: calc(var(--gap) * -1);
                    width: calc(100% - (var(--gap) * 0.4));
                    height: calc(100% - (var(--gap) * 0.4));
                  }
                `}
              />
              <div
                css={css`
                  padding: 20px 22px;
                  background: white;
                  border-radius: 20px;
                  display: flex;
                  flex-direction: column;
                  align-items: center;
                  justify-content: center;
                  max-width: 500px;
                  border: 0.98px solid rgba(255, 255, 255, 0.1);

                  ${mq["xs"]} {
                    max-width: 560px;
                    // border-radius: 11.3px;
                    padding: 12px;
                  }
                  ${mq["sm"]} {
                    max-width: 500px;
                  }
                  ${mq["md"]} {
                    max-width: 500px;
                  }
                  ${mq["lg"]} {
                    max-width: 600px;
                  }
                  ${mq["xl"]} {
                    max-width: 700px;
                  }
                `}
              >
                <div
                  css={css`
                    padding: 40px 44px;
                    background: white !important;
                    border-radius: 10px;
                  `}
                >
                  <Heading
                    level={6}
                    css={css`
                      color: ${darkColor};
                      text-align: center;
                      width: 100%;
                      ${H7Css} ${mq["lg"]} {
                        ${H5Css}
                      }
                    `}
                  >
                    <Balancer>
                      Get Started Creating Your Schedules for Free
                    </Balancer>
                  </Heading>
                  <BodyMedium14
                    css={css`
                      color: #666;
                      font-size: 14px;
                      margin-top: 12px;
                      text-align: center;
                    `}
                  >
                    Enter basic information about your organization to get
                    started. You can make changes later if needed.
                  </BodyMedium14>
                  <form
                    onSubmit={handleSubmit}
                    css={css`
                      display: flex;
                      flex-direction: column;
                      align-items: center;
                      gap: 20px;
                      width: 100%;
                      max-width: 400px;
                      margin-top: 30px;
                      ${mq["sm"]} {
                        flex-direction: row;
                      }
                      ${mq["md"]} {
                        flex-direction: row;
                        gap: 0px;
                      }
                      ${mq["xs"]} {
                        gap: 30px;
                      }

                      ${mq["xs"]} {
                        gap: 30px;
                      }
                      ${mq["lg"]} {
                        flex-direction: column;
                        gap: 30px;
                      }
                    `}
                  >
                    <Input
                      autoFocus
                      placeholder="Your Organization Name"
                      required
                      fullWidth
                    />

                    <Button jwType="dark" type="submit" fullWidth>
                      Start Scheduling
                    </Button>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Container>
  );
};

export default ScheduleGeneratorHeroSection;
